import React from "react";
import { X, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

/**
 * MaterialInfoDialog — modal explaining materials (Giga Grip Latex / Neoprene Cuff).
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - material: "german_latex" | "neoprene"
 */
const MaterialInfoDialog = ({ open, onOpenChange, material }) => {
  const { t } = useLanguage();
  const info = t.materials[material];
  if (!info) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`material-dialog-${material}`}
        className="bg-[#0A0A0A] border border-[#00A651]/30 text-white rounded-none max-w-xl p-0 overflow-hidden"
      >
        <div className="relative p-8 md:p-10">
          <DialogClose
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#00A651] hover:text-[#00A651] transition-colors"
            data-testid={`material-dialog-close-${material}`}
            aria-label={t.close}
          >
            <X className="w-4 h-4" />
          </DialogClose>

          <div className="inline-block bg-[#00A651] text-black font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-5">
            {material === "german_latex" ? "Material" : "Construction"}
          </div>

          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-brush text-4xl md:text-5xl text-[#00A651] leading-tight uppercase">
              {info.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 font-condensed uppercase tracking-widest text-xs">
              {info.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-5 text-white/80 text-base leading-relaxed">
            <p>{info.body_1}</p>
            <p className="font-condensed uppercase tracking-widest text-xs text-white/50 pt-2">
              {info.body_2}
            </p>
            <ul className="space-y-2.5">
              {info.points.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 text-[#00A651] shrink-0" />
                  <span className="text-white/80">{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-white/50 italic border-t border-white/10 pt-4 mt-6">
              {info.footer}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialInfoDialog;
